import { useCallback } from "react";
import { useAxiosInstances } from "../../axios-instance";
import {
  IExtracao,
  IExtracaoTableItem,
  IFiltrosProjeto,
  IListaDeduplicacao,
  IProjeto,
  IRecorte,
} from "../../../application/models";

export const useExtracaoApi = () => {
  // Dispatch
  const { axiosAuthInstance } = useAxiosInstances();

  const createExtracaoApi = useCallback(
    async (data: {
      idVelesGeoProjeto: number;
      idUsuario: number;
      nome: string;
      quantidade?: number;
      filtros?: IFiltrosProjeto | null;
      recortes?: IRecorte[] | null;
      idAcesso: number;
    }): Promise<IExtracao> => {
      try {
   
        const result = await axiosAuthInstance.post("/geo-veles/extracao", {
          idVelesGeoProjeto: data.idVelesGeoProjeto,
          idUsuario: data.idUsuario,
          nome: data.nome,
          quantidade: data.quantidade,
          filtros: data.filtros,
          recortes: data.recortes,
          idAcesso: data.idAcesso,
        });

        return result.data.extracao;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const findExtracaoListApi = useCallback(
    async (data: {
      pagination: {
        page: number;
        pageSize: number;
      };
      filter: {
        idUsuario: number;
        idVelesPerfilInterno: number;
        inclusaoMax?: Date;
        inclusaoMin?: Date;
        nome?: string;
        usuario?: string;
        cliente?: string;
        nomeUsuario?: string;
      };
    }): Promise<{
      extracoes: IExtracaoTableItem[];
      page: number;
      limit: number;
      total: number;
    }> => {
      try {
        const result = await axiosAuthInstance.get("/geo-veles/extracao", {
          params: {
            idUsuario: data.filter.idUsuario,
            idVelesPerfilInterno: data.filter.idVelesPerfilInterno,
            inclusaoMax: data.filter.inclusaoMax,
            inclusaoMin: data.filter.inclusaoMin,
            limit: data.pagination.pageSize,
            page: data.pagination.page,
            nome: data.filter.nome,
            usuario: data.filter.usuario,
            cliente: data.filter.cliente,
            nomeUsuario: data.filter.nomeUsuario,
          },
        });

        return {
          limit: result.data.limit,
          page: result.data.page,
          extracoes: result.data.extracoes,
          total: result.data.total,
        };
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const findProjetobyIdVelesGeoExtracaoApi = useCallback(
    async (data: { idVelesGeoExtracao: number }): Promise<IProjeto> => {
      try {
        const response = await axiosAuthInstance.get(
          `/geo-veles/extracao/projetoExtracao/${data.idVelesGeoExtracao}`
        );
        return response.data.projeto;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const downloadExtracaoApi = useCallback(
    async (data: {
      IdVelesGeoExtracao: number;
      IdUsuario: number;
      IdVelesGeoLayoutExtracao: number;
      fileType: string;
      Nome: string;
    }) => {
      try {
        const response = await axiosAuthInstance.get(
          "/geo-veles/extracao/projeto/download",
          {
            responseType: "blob",
            params: {
              IdVelesGeoExtracao: data.IdVelesGeoExtracao,
              IdUsuario: data.IdUsuario,
              IdVelesGeoLayoutExtracao: data.IdVelesGeoLayoutExtracao,
              fileType: data.fileType,
            },
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${data.Nome}.${data.fileType}`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const verifyExtracoByNomeApi = useCallback(
    async (data: {
      nome: string;
      idUsuario: number;
      idVelesGeoProjeto: number;
    }): Promise<boolean> => {
      try {
        const result = await axiosAuthInstance.get(
          "/geo-veles/extracao/verifyNome",
          {
            params: {
              nome: data.nome,
              idUsuario: data.idUsuario,
              idVelesGeoProjeto: data.idVelesGeoProjeto,
            },
          }
        );
        if (result.data.exists) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const findProjetosbyIdVelesGeoExtracaoApi = useCallback(
    async (data: { idVelesGeoExtracao: number[] }): Promise<IProjeto[]> => {
      try {
        const response = await axiosAuthInstance.post(
          "/geo-veles/extracao/projetoExtracao/ids",  data.idVelesGeoExtracao,
        );
        return response.data.projetos;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const getVerifyDuplicateCPFsApi = useCallback(
    async (data: {
      idVelesGeoExtracao: number;
      idUsuario: number;
      cpfList: IListaDeduplicacao[];
    }): Promise<string[]> => {
      try {
        const response = await axiosAuthInstance.post(
          "/geo-veles/extracao/projetoExtracao/verifyCPFsDuplicates/",
          {
            idVelesGeoExtracao: data.idVelesGeoExtracao,
            idUsuario: data.idUsuario,
            cpfList: data.cpfList,
          }
        );

        const cpfsDuplicados = response.data?.cpfsDuplicados;

        if (!Array.isArray(cpfsDuplicados)) {
          throw new Error(
            "Resposta da API inválida. Esperado 'cpfsDuplicados' como array."
          );
        }

        return cpfsDuplicados;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  return {
    createExtracaoApi,
    findExtracaoListApi,
    downloadExtracaoApi,
    findProjetobyIdVelesGeoExtracaoApi,
    verifyExtracoByNomeApi,
    findProjetosbyIdVelesGeoExtracaoApi,
    getVerifyDuplicateCPFsApi,
  };
};
